import { yupResolver } from "@hookform/resolvers/yup";
import { ContentCopyRounded, EditRounded } from "@mui/icons-material";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, IconButton, Radio, Stack } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ICellRendererParams } from "ag-grid-community";
import dayjs from "dayjs";
import _ from "lodash";
import { useCallback, useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CustomLoader from "../../../../../../util/component/CustomLoader";
import { setSnackbar } from "../../../../../../util/state/globalAction";
import { GlobalContext } from "../../../../../../util/state/GlobalContextProvider";
import { snackbarTypes } from "../../../../../../util/state/globalVariables";
import { getAncillaries, getCarGroups, getCategories, getLocations, getProducts, getRates, getSalesChannels, getSegments, updateRule } from "../../../../api/services";
import DialogCloseButton from "../../../../components/DialogCloseButton";
import { FormCheckbox, FormDateTimePicker, FormNumberInput, FormRadioGroup, FormTextField } from "../../../../components/fields";
import { BookingScope, IRule, ReactQueryKey, RuleType } from "../../../../types";
import { hasScope, LOCALE_DATE_TIME_FORMAT } from "../../../../utils";
import { ruleValidationSchema } from "../../form";
import { IFormValuesRule, RuleConstraint } from "../../types";
import { getDefaultConstraints } from "../../utils";
import ConstraintList from "../../_components/constraint-list/ConstraintList";
import { RulesProvider } from "../../_context/RulesContext";
import { MailingList } from "../../_rule_fields";
import { rulesFilterState } from "../../recoil";
import { useRecoilValue } from "recoil";

const CellActions = (params: ICellRendererParams<IRule>) => {

    const { t } = useTranslation('booking');

    const navigate = useNavigate();

    const { dispatchGlobalData, globalData } = useContext(GlobalContext);
    const rulesFilter = useRecoilValue(rulesFilterState);
    const { tenants, dealers } = rulesFilter || {};

    const form = useForm<IFormValuesRule>({
        defaultValues: {
            type: params.data?.type,
            name: params.data?.name ?? '',
            bookingDateFrom: params.data?.startDateTime ? dayjs(params.data?.startDateTime) : null,
            bookingDateTo: params.data?.endDateTime ? dayjs(params.data?.endDateTime) : null,
            skip: params.data?.skip,
            applyFor: params.data?.applyFor === null ? 0 : params.data?.applyFor,
            mailingList: params.data?.mailingList,
            alertOnSetting: params.data?.alertOnSetting,
            alertOnActivation: params.data?.alertOnActivation,
            pickupDateTimeFrom: {
                value: params.data?.pickupDateTimeFrom ? dayjs(params.data.pickupDateTimeFrom) : null,
                excluded: params.data?.excludedPickupDateTimeFrom
            },
            pickupDateTimeTo: {
                value: params.data?.pickupDateTimeTo ? dayjs(params.data.pickupDateTimeTo) : null,
                excluded: params.data?.excludedPickupDateTimeTo
            },
            dropoffDateTimeFrom: {
                value: params.data?.dropoffDateTimeFrom ? dayjs(params.data.dropoffDateTimeFrom) : null,
                excluded: params.data?.excludedDropoffDateTimeFrom
            },
            dropoffDateTimeTo: {
                value: params.data?.dropoffDateTimeTo ? dayjs(params.data.dropoffDateTimeTo) : null,
                excluded: params.data?.excludedDropoffDateTimeTo
            },
            includingDateTimeFrom: {
                value: params.data?.includingDateTimeFrom ? dayjs(params.data.includingDateTimeFrom) : null,
                excluded: params.data?.excludedIncludingDateTimeFrom
            },
            includingDateTimeTo: {
                value: params.data?.includingDateTimeTo ? dayjs(params.data.includingDateTimeTo) : null,
                excluded: params.data?.excludedIncludingDateTimeTo
            },
            pickupLocations: {
                value: [],
                excluded: params.data?.excludedPickupLocations
            },
            dropoffLocations: {
                value: [],
                excluded: params.data?.excludedDropoffLocations
            },
            pickupAreas: {
                value: params.data?.pickupAreas,
                excluded: params.data?.excludedPickupAreas
            },
            dropoffAreas: {
                value: params.data?.dropoffAreas,
                excluded: params.data?.excludedDropoffAreas
            },
            pickupRegions: {
                value: params.data?.pickupRegions,
                excluded: params.data?.excludedPickupRegions
            },
            dropoffRegions: {
                value: params.data?.dropoffRegions,
                excluded: params.data?.excludedDropoffRegions
            },
            pickupZones: {
                value: params.data?.pickupZones,
                excluded: params.data?.excludedPickupZones
            },
            dropoffZones: {
                value: params.data?.dropoffZones,
                excluded: params.data?.excludedDropoffZones
            },
            acrissList: {
                value: params.data?.acrissList,
                excluded: params.data?.excludedAcrissList
            },
            sippList: {
                value: params.data?.sippList,
                excluded: params.data?.excludedSippList
            },
            customerCodes: {
                value: params.data?.customerCodes,
                excluded: params.data?.excludedCustomerCodes
            },
            salesChannels: {
                value: [],
                excluded: params.data?.excludedSalesChannels
            },
            segments: {
                value: [],
                excluded: params.data?.excludedSegments
            },
            categories: {
                value: [],
                excluded: params.data?.excludedCategories
            },
            products: {
                value: [],
                excluded: params.data?.excludedProducts
            },
            rates: {
                value: [],
                excluded: params.data?.excludedRates
            },
            ancillaries: {
                value: [],
                excluded: params.data?.excludedAncillaries
            },
            minDuration: {
                value: params.data?.minDuration ?? 0,
                excluded: params.data?.excludedMinDuration
            },
            maxDuration: {
                value: params.data?.maxDuration ?? 0,
                excluded: params.data?.excludedMaxDuration
            },
            minNoticeDays: {
                value: params.data?.minNoticeDays ?? 0,
                excluded: params.data?.excludedMinNoticeDays
            },
            maxNoticeHours: {
                value: params.data?.maxNoticeHours ?? 0,
                excluded: params.data?.excludedMaxNoticeHours
            },
            tenants: params.data?.tenants,
            dealers: params.data?.dealers,
        },
        resolver: yupResolver(
            ruleValidationSchema,
            {},
            {
                rawValues: true
            }
        ),
        shouldUnregister: true
    });

    const updateRuleMutation = useMutation(
        updateRule,
        {
            onSuccess: () => {
                params.api.refreshServerSide();
            },
            onError: () => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.error.default'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            }
        },
    );

    useQuery({
        queryKey: [ReactQueryKey.Locations, tenants, dealers],
        queryFn: () => getLocations({ tenants, dealers }),
        onSuccess: (data) => {
            if (params.data?.pickupLocations && params.data?.pickupLocations.length > 0) {
                form.register('pickupLocations.value');
                form.resetField('pickupLocations.value', {
                    defaultValue: params.data?.pickupLocations
                        .map(v => data.locations
                            .find(w => w.id === v) ?? { id: v, label: v }),
                });
            }
            if (params.data?.dropoffLocations && params.data?.dropoffLocations.length > 0) {
                form.register('dropoffLocations.value');
                form.resetField('dropoffLocations.value', {
                    defaultValue: params.data?.dropoffLocations
                        .map(v => data.locations
                            .find(w => w.id === v) ?? { id: v, label: v }),
                });
            }
        },
        retry: false,
        refetchOnWindowFocus: false,
        refetchOnMount: true
    });
    useQuery({
        queryKey: [ReactQueryKey.CarGroups, tenants, dealers],
        queryFn: () => getCarGroups({ tenants, dealers }),
        retry: false,
        refetchOnWindowFocus: false,
        refetchOnMount: true
    });
    useQuery({
        queryKey: [ReactQueryKey.Segments, tenants, dealers],
        queryFn: () => getSegments({ tenants, dealers }),
        onSuccess: (data) => {
            if (params.data?.segments && params.data?.segments.length > 0) {
                form.register('segments.value');
                form.resetField('segments.value', {
                    defaultValue: params.data?.segments
                        .map(v => data.list
                            .find(w => w.code === v) ?? { code: v, label: v }),
                });
            }
        },
        retry: false,
        refetchOnWindowFocus: false,
        refetchOnMount: true
    });
    useQuery({
        queryKey: [ReactQueryKey.Categories, tenants, dealers],
        queryFn: () => getCategories({ tenants, dealers }),
        onSuccess: (data) => {
            if (params.data?.categories && params.data?.categories.length > 0) {
                form.register('categories.value');
                form.resetField('categories.value', {
                    defaultValue: params.data?.categories
                        .map(v => data.list
                            .find(w => w.code === v) ?? { code: v, label: v }),

                });
            }
        },
        retry: false,
        refetchOnWindowFocus: false,
        refetchOnMount: true
    });
    useQuery({
        queryKey: [ReactQueryKey.Products, tenants, dealers],
        queryFn: () => getProducts({ tenants, dealers }),
        onSuccess: (data) => {
            if (params.data?.products && params.data?.products.length > 0) {
                form.register('products.value');
                form.resetField('products.value', {
                    defaultValue: params.data?.products
                        .map(v => data.list
                            .find(w => w.code === v) ?? { code: v, label: v }),
                });
            }
        },
        retry: false,
        refetchOnWindowFocus: false,
        refetchOnMount: true
    });
    useQuery({
        queryKey: [ReactQueryKey.Rates, tenants, dealers],
        queryFn: () => getRates({ tenants, dealers }),
        onSuccess: (data) => {
            if (params.data?.rates && params.data?.rates.length > 0) {
                form.register('rates.value');
                form.resetField('rates.value', {
                    defaultValue: params.data?.rates
                        .map(v => data.list
                            .find(w => w.code === v) ?? { code: v, label: v }),
                });
            }
        },
        retry: false,
        refetchOnWindowFocus: false,
        refetchOnMount: true
    });
    useQuery({
        queryKey: [ReactQueryKey.Ancillaries, tenants, dealers],
        queryFn: () => getAncillaries({ tenants, dealers }),
        onSuccess: (data) => {
            if (params.data?.ancillaries && params.data?.ancillaries.length > 0) {
                form.register('ancillaries.value');
                form.resetField('ancillaries.value', {
                    defaultValue: params.data?.ancillaries
                        .map(v => data.list
                            .find(w => w.code === v) ?? { code: v, label: v }),
                });
            }
        },
        retry: false,
        refetchOnWindowFocus: false,
        refetchOnMount: true
    });
    useQuery({
        queryKey: [ReactQueryKey.SalesChannels, tenants, dealers],
        queryFn: () => getSalesChannels({ tenants, dealers }),
        onSuccess: (data) => {
            if (params.data?.salesChannels && params.data?.salesChannels.length > 0) {
                form.register('salesChannels.value');
                form.resetField('salesChannels.value', {
                    defaultValue: params.data?.salesChannels
                        .map(v => data
                            .find(w => w.code === v) ?? { code: v, label: v }),
                });
            }
        },
        retry: false,
        refetchOnWindowFocus: false,
        refetchOnMount: true
    });

    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [currentDate, setCurrentDate] = useState<dayjs.Dayjs>(dayjs());
    const [constraints, setConstraints] = useState<RuleConstraint[]>(getDefaultConstraints(params.data));

    const handleCloseDialog = useCallback(() => {
        setDialogOpen(false);
    }, []);
    const handleShowDialog = useCallback(() => {
        setDialogOpen(true);
        setCurrentDate(dayjs());
        form.register('type');
    }, []);
    const resetConstraints = useCallback(() => {
        setConstraints(getDefaultConstraints(params.data));
    }, []);
    const handleSave = useCallback((data: IFormValuesRule) => {
        if (constraints.length === 0) {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('message.error.no_rule_parameters_selected'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            },)
            return;
        }
        if (
            params.data?.id !== undefined &&
            data.bookingDateFrom !== null
        ) {
            setDialogOpen(false);
            updateRuleMutation.mutate({
                id: params.data.id,
                fields: {
                    type: data.type,
                    name: data.name,
                    startDateTime: data.bookingDateFrom.toISOString(),
                    endDateTime: data.bookingDateTo ? data.bookingDateTo.toISOString() : null,
                    skip: data.skip,
                    applyFor: data.applyFor !== undefined ? data.applyFor : null,
                    mailingList: data.mailingList ?? null,
                    alertOnSetting: data.alertOnSetting !== undefined ? data.alertOnSetting : null,
                    alertOnActivation: data.alertOnActivation !== undefined ? data.alertOnActivation : null,
                    pickupDateTimeFrom: (data.pickupDateTimeFrom && data.pickupDateTimeFrom.value) ? {
                        value: data.pickupDateTimeFrom.value.format(LOCALE_DATE_TIME_FORMAT),
                        excluded: data.pickupDateTimeFrom.excluded
                    } : null,
                    pickupDateTimeTo: (data.pickupDateTimeTo && data.pickupDateTimeTo.value) ? {
                        value: data.pickupDateTimeTo.value.format(LOCALE_DATE_TIME_FORMAT),
                        excluded: data.pickupDateTimeTo.excluded
                    } : null,
                    dropoffDateTimeFrom: (data.dropoffDateTimeFrom && data.dropoffDateTimeFrom.value) ? {
                        value: data.dropoffDateTimeFrom.value.format(LOCALE_DATE_TIME_FORMAT),
                        excluded: data.dropoffDateTimeFrom.excluded
                    } : null,
                    dropoffDateTimeTo: (data.dropoffDateTimeTo && data.dropoffDateTimeTo.value) ? {
                        value: data.dropoffDateTimeTo.value.format(LOCALE_DATE_TIME_FORMAT),
                        excluded: data.dropoffDateTimeTo.excluded
                    } : null,
                    includingDateTimeFrom: (data.includingDateTimeFrom && data.includingDateTimeFrom.value) ? {
                        value: data.includingDateTimeFrom.value.format(LOCALE_DATE_TIME_FORMAT),
                        excluded: data.includingDateTimeFrom.excluded
                    } : null,
                    includingDateTimeTo: (data.includingDateTimeTo && data.includingDateTimeTo.value) ? {
                        value: data.includingDateTimeTo.value.format(LOCALE_DATE_TIME_FORMAT),
                        excluded: data.includingDateTimeTo.excluded
                    } : null,
                    pickupLocations: (data.pickupLocations && data.pickupLocations.value) ? {
                        value: data.pickupLocations.value.map(pl => pl.id),
                        excluded: data.pickupLocations?.excluded
                    } : null,
                    dropoffLocations: (data.dropoffLocations && data.dropoffLocations.value) ? {
                        value: data.dropoffLocations.value.map(pl => pl.id),
                        excluded: data.dropoffLocations?.excluded
                    } : null,
                    pickupAreas: data.pickupAreas ?? null,
                    dropoffAreas: data.dropoffAreas ?? null,
                    pickupRegions: data.pickupRegions ?? null,
                    dropoffRegions: data.dropoffRegions ?? null,
                    pickupZones: data.pickupZones ?? null,
                    dropoffZones: data.dropoffZones ?? null,
                    acrissList: data.acrissList ?? null,
                    sippList: data.sippList ?? null,
                    customerCodes: data.customerCodes ?? null,
                    salesChannels: data.salesChannels ? {
                        value: data.salesChannels.value.map(s => s.code),
                        excluded: data.salesChannels.excluded
                    } : null,
                    segments: data.segments ? {
                        value: data.segments.value.map(s => s.code),
                        excluded: data.segments.excluded
                    } : null,
                    categories: data.categories ? {
                        value: data.categories.value.map(c => c.code),
                        excluded: data.categories.excluded
                    } : null,
                    products: data.products ? {
                        value: data.products.value.map(s => s.code),
                        excluded: data.products.excluded
                    } : null,
                    rates: data.rates ? {
                        value: data.rates.value.map(r => r.code),
                        excluded: data.rates.excluded
                    } : null,
                    ancillaries: data.ancillaries ? {
                        value: data.ancillaries.value.map(a => a.code),
                        excluded: data.ancillaries.excluded
                    } : null,
                    minDuration: data.minDuration ?? null,
                    maxDuration: data.maxDuration ?? null,
                    minNoticeDays: data.minNoticeDays ?? null,
                    maxNoticeHours: data.maxNoticeHours ?? null,
                    tenants: params.data?.tenants ? {
                        value: params.data.tenants,
                        excluded: false
                    } : null,
                    dealers: params.data?.dealers ? {
                        value: params.data.dealers,
                        excluded: false
                    } : null
                }
            })
        }
    }, [params.data?.id, constraints]);

    useEffect(() => {
        if (!form.formState.isSubmitting) {
            if (!_.isEmpty(form.formState.errors)) {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.error.form'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            }
        }
    }, [form.formState.isSubmitting]);

    return (
        <>
            <Stack direction="row" alignItems="center" height="100%">
                {hasScope(globalData, BookingScope.CreateEditRule) &&
                    <IconButton title={t('label.edit')} onClick={handleShowDialog} color="accent">
                        <EditRounded fontSize="small" />
                    </IconButton>
                }
                {hasScope(globalData, BookingScope.CreateEditRule) &&
                    <IconButton
                        title={t('label.clone')}
                        color="accent"
                        onClick={() => navigate('/booking/rules/insert', {
                            state: {
                                rule: params.data
                            }
                        })}
                    >
                        <ContentCopyRounded />
                    </IconButton>
                }
            </Stack>
            <Dialog
                open={dialogOpen}
                fullWidth
                maxWidth="lg"
                TransitionProps={{
                    onExited: resetConstraints
                }}
            >
                <DialogCloseButton onClick={handleCloseDialog} />
                <DialogTitle>{params.data?.name}</DialogTitle>
                <DialogContent>
                    <FormProvider {...form}>
                        <Stack rowGap={2}>
                            <FormRadioGroup<IFormValuesRule>
                                name="type"
                                control={form.control}
                                label={t('label.type')}
                            >
                                <FormControlLabel
                                    value={RuleType.Pending}
                                    label={t('label.pending')}
                                    disabled
                                    control={<Radio />}
                                />
                                <FormControlLabel
                                    value={RuleType.StopSells}
                                    label={t('label.stopSell')}
                                    disabled
                                    control={<Radio />}
                                />
                            </FormRadioGroup>
                            <Grid container columnSpacing={3} rowSpacing={3} alignItems="start">
                                <Grid item xs={12} lg={3}>
                                    <FormTextField<IFormValuesRule>
                                        name="name"
                                        control={form.control}
                                        label={t('label.name')}
                                    />
                                </Grid>
                                <Grid item xs={12} lg={3}>
                                    <FormDateTimePicker<IFormValuesRule>
                                        name="bookingDateFrom"
                                        control={form.control}
                                        label={t('label.bookingDateFrom')}
                                        minDateTime={currentDate}
                                    />
                                </Grid>
                                <Grid item xs={12} lg={3}>
                                    <FormDateTimePicker<IFormValuesRule>
                                        name="bookingDateTo"
                                        control={form.control}
                                        label={t('label.bookingDateTo')}
                                        minDateTime={currentDate}
                                    />
                                </Grid>
                                <Grid item xs={12} lg={3}>
                                    <FormNumberInput<IFormValuesRule>
                                        name="skip"
                                        control={form.control}
                                        label={t('label.skip')}
                                        minValue={0}
                                    />
                                </Grid>
                                {form.watch('type') === RuleType.Pending &&
                                    <Grid item xs={12} lg={3}>
                                        <FormNumberInput<IFormValuesRule>
                                            name="applyFor"
                                            control={form.control}
                                            label={t('label.applyFor')}
                                            minValue={0}
                                        />
                                    </Grid>
                                }
                            </Grid>
                            {form.watch('type') === RuleType.StopSells &&
                                <Grid container columnSpacing={3} rowSpacing={3} alignItems="end">
                                    <Grid item xs={12} lg={3}>
                                        <MailingList />
                                    </Grid>
                                    <Grid item xs={12} lg={3}>
                                        <FormCheckbox<IFormValuesRule>
                                            name="alertOnSetting"
                                            control={form.control}
                                            label={t('label.alertOnSetting')}
                                        />
                                    </Grid>
                                    <Grid item xs={12} lg={3}>
                                        <FormCheckbox<IFormValuesRule>
                                            name="alertOnActivation"
                                            control={form.control}
                                            label={t('label.alertOnActivation')}
                                        />
                                    </Grid>
                                </Grid>
                            }
                            <RulesProvider
                                constraints={constraints}
                                setConstraints={setConstraints}
                            >
                                <ConstraintList />
                            </RulesProvider>
                        </Stack>
                    </FormProvider>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={form.handleSubmit(handleSave)}>Save</Button>
                </DialogActions>
            </Dialog>
            <CustomLoader loading={updateRuleMutation.isLoading} />
        </>
    );
}

export default CellActions;